import type { FC } from 'react';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { analytics } from '@src/utility/analytics';

import AIGenerateCard from '../common/AIGenerateCard';

import type { PunchListItem, Story } from '../../types.ts/story';
import type {
  AIProducerCard,
  //PunchListImageGeneration,
} from '../../types.ts/general';

import FeelingLuckyIcon from '../../svgs/FeelingLuckyIcon';
import SpinningLoading from '../SpinningLoading';
//import TriggerPunchListGenerateModal from './TriggerPunchListGenerateModal';
import type { TranscriptElement } from '../../types.ts/transcript';
import ChatGPTService from '../../services/ChatGPTService';
import PunchListContentItem from './PunchListContentItem';
import type { ModalItem } from '../common/GeneratedPunchListModal';
import GeneratedPunchListModal from '../common/GeneratedPunchListModal';

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import FeelingLuckyModal, { ProceedButton } from './FeelingLuckyModal';
import { useDatoClient, useTranscription } from '@src/stores-v2/StoreContext';

type Props = {
  story?: Story;
  selectedCard?: AIProducerCard;
};

type TargetPosition = {
  transcriptPosition: {
    startIndex: number;
    endIndex: number;
  };
  duration: number;
  startTime: number;
  endTime: number;
};

const AIPhotoPunchListProducer: FC<Props> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const transcriptionStore = useTranscription();

  const [generatingMissingAiImage, setGeneratingMissingAiImage] =
    useState<boolean>(false);
  //const [openGeneratedImages, toggleGeneratedImages] =
  //  useState<PunchListImageGeneration | null>(null);
  const [selectedPunchListId, setSelectedPunchListId] = useState<string>();

  const [selectedAction, setSelectedAction] = useState<
    'photoHunt' | 'feelingLucky' | null
  >('photoHunt');
  const [modalItem, toggleModal] = useState<ModalItem>({
    item: null,
    archive: null,
  });

  const punchList = videoCreator.punchListManager.punchListItems;
  const punchListGenerateCount = videoCreator.punchListGenerateCount;

  const [loading, setLoading] = useState(false);

  const getTsRangeFromFinalTranscription = (
    startElementIndex: number,
    endElementIndex: number,
  ): { ts: number | null; end_ts: number | null } => {
    let ts = null;
    let end_ts = null;

    // loop through all transcript elements and track the index of the target sentence across multiple elements
    let currElementIndex = startElementIndex;
    while (currElementIndex <= endElementIndex) {
      if (transcriptionStore.getFinalTranscriptionElements()) {
        const currElementFinal =
          transcriptionStore.getFinalTranscriptionElements()[currElementIndex];

        if (currElementFinal) {
          if (!ts && currElementFinal.ts) {
            ts = currElementFinal.ts;
          }
          if (currElementFinal.end_ts) {
            end_ts = currElementFinal.end_ts;
          }
        }
      }
      currElementIndex++;
    }

    return { ts, end_ts };
  };

  function getPunchListItemPosition(
    punchListItem: PunchListItem,
    transcriptElements?: TranscriptElement[],
  ): TargetPosition | null {
    let targetSentence = punchListItem.line;
    // loop through all transcript elements and track the index of the target sentence across multiple elements
    if (!transcriptElements || !targetSentence) return null;

    let targetSentenceCurrIndex = 0;
    let currElementIndex = 0;
    let targetSentenceElementStartIndex = 0;
    let targetSentenceElementEndIndex = 0;
    let found = false;
    while (currElementIndex < (transcriptElements?.length - 1 || 0)) {
      let currElementValue = transcriptElements[currElementIndex].value;

      // if element contains the start of the target sentence save it as the start of the index
      if (
        currElementValue &&
        transcriptElements[currElementIndex]?.state !== 'removed' &&
        transcriptElements[currElementIndex]?.state !== 'cut'
      ) {
        // console.log('comparing X', targetSentence.substring(targetSentenceCurrIndex, targetSentenceCurrIndex + currElementValue.length).length, 'X to X', currElementValue.length, 'X')
        if (
          targetSentence.substring(
            targetSentenceCurrIndex,
            targetSentenceCurrIndex + currElementValue.length,
          ) === currElementValue
        ) {
          if (targetSentenceCurrIndex === 0) {
            targetSentenceElementStartIndex = currElementIndex;
          }
          targetSentenceCurrIndex += currElementValue.length;
        } else {
          targetSentenceCurrIndex = 0;
        }
      }
      if (targetSentenceCurrIndex === targetSentence.length) {
        targetSentenceElementEndIndex = currElementIndex;
        found = true;
        // punchListItem.found = true;
        break;
      }
      currElementIndex++;
    }

    // RETURN if not found
    if (!found) return null;

    const { ts, end_ts } = getTsRangeFromFinalTranscription(
      targetSentenceElementStartIndex,
      targetSentenceElementEndIndex,
    );

    if (!ts || !end_ts) return null;

    return {
      transcriptPosition: {
        startIndex: targetSentenceElementStartIndex,
        endIndex: targetSentenceElementEndIndex,
      },
      duration: Math.min(8, end_ts - ts),
      startTime: ts,
      endTime: end_ts,
    };
  }

  function closeGapsInPhotohuntPositions(
    targetPositions: (TargetPosition | null)[],
    transcriptElements?: TranscriptElement[],
  ): (TargetPosition | null)[] {
    if (!transcriptElements) return targetPositions;

    return targetPositions.map((targetPosition, index, array) => {
      if (index === array.length - 1 || !targetPosition || !array[index + 1]) {
        return targetPosition;
      }

      const nextTargetStartIndex =
        array[index + 1]!.transcriptPosition.startIndex;
      const { ts: nextTargetStartTs } = getTsRangeFromFinalTranscription(
        nextTargetStartIndex,
        nextTargetStartIndex,
      );
      if (
        nextTargetStartTs != null &&
        nextTargetStartTs - targetPosition.endTime <= 4
      ) {
        targetPosition.transcriptPosition.endIndex = nextTargetStartIndex - 1;
        targetPosition.endTime = nextTargetStartTs;
        targetPosition.duration =
          targetPosition.endTime - targetPosition.startTime;
      }

      return targetPosition;
    });
  }

  // todo move to separate class (AIProducer?)
  const handleGeneratePhotoPunchList = async () => {
    try {
      setLoading(true);
      // SAVE CURRENT VIDEO BEFORE GENERATING PUNCHLIST

      // analytics
      analytics.track('generate_photo_punchlist', {
        storyId: props.story?.id,
        storyTitle: props.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
        showcaseId: props.story?.primaryShowcase?.id,
        showcaseTitle: props.story?.primaryShowcase?.title,
      });

      // delete elments that are related to the current photo punchlist
      let punch_list_element_ids =
        punchList
          ?.filter(pld => pld.sub_type !== 'manual')
          ?.map(pld => pld.id) || [];

      let to_delete = videoCreator.state?.elements.filter(
        e => e.source.id && punch_list_element_ids.includes(e.source.id),
      );
      let to_delete_ids = to_delete?.map(e => e.source.id);

      if (to_delete_ids) {
        for (let i = 0; i < to_delete_ids.length; i++) {
          const id = to_delete_ids[i];
          await videoCreator.deleteElementWithTranscription(id, false);
        }
      }
    } catch (error) {
      console.error('test Failed to delete elements', error);
      setLoading(false);
      return;
    }

    const gptService = new ChatGPTService(
      videoCreator,
      datoClientStore.aiPromptRepository,
    );
    videoCreator.punchListLoading = true;
    try {
      await gptService.regenerateStreamResponse({
        key: 'Photo Punchlist',
        setLoading: async value => {
          videoCreator.punchListLoading = value;
          setLoading(value);
          if (!value) {
            await proceedWithResponseToGeneratePunchList(
              getPunchListFromRawResponse(),
            );
          }
        },
        version: '2',
      });
    } catch (error) {
      console.error('test Failed to generate punch list using stream', error);
      videoCreator.punchListLoading = false;
      setLoading(false);
      return;
    }
  };

  const proceedWithResponseToGeneratePunchList = async (
    punchListResponse: PunchListItem[] | undefined,
  ) => {
    if (!punchListResponse) return;

    const punchListData = punchListResponse?.map((obj: any) => {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          `${key.slice(0, 1).toLowerCase()}${key.slice(1)}`,
          value,
        ]),
      );
    }) as PunchListItem[];
    const formattedResponse = { punchList: punchListData };

    if (!formattedResponse) throw new Error('No response from GPT');

    // find the transcript elements that match the target sentence and set a flag to highlight in the
    // transcript
    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();

    let targetPositions: (TargetPosition | null)[] =
      formattedResponse.punchList.map(punchListItem => {
        return getPunchListItemPosition(punchListItem, transcriptElements);
      });
    targetPositions = closeGapsInPhotohuntPositions(
      targetPositions,
      transcriptElements,
    );

    const targetTrack = videoCreator.getMaxTrack() + 1;
    for (let i = 0; i < targetPositions.length; i++) {
      const punchListItem = formattedResponse.punchList[i];
      const targetPosition = targetPositions[i];
      if (
        targetPosition &&
        targetPosition.duration > 0 &&
        targetPosition.startTime >= 0
      ) {
        // todo set into current video punchList
        // add changes to transcript
        videoCreator.videoTranscriptionProcessor.addPhotoHighlight(
          targetPosition.transcriptPosition.startIndex,
          targetPosition.transcriptPosition.endIndex,
          punchListItem.id!,
        );

        await videoCreator.punchListManager.addPunchListItemToTimeline(
          punchListItem,
          targetPosition.duration,
          targetPosition.startTime,
          targetPosition.endTime,
          targetTrack,
        );
        punchListItem.transcriptPosition = targetPosition.transcriptPosition;
      } else {
        console.log('could not add', punchListItem, 'in transcript');
      }
    }

    formattedResponse.punchList = formattedResponse.punchList.filter(
      punchListItem => punchListItem.transcriptPosition,
    );

    videoCreator.punchListManager.punchListItems = (punchList || [])
      .filter(p => p.sub_type === 'manual')
      .concat(formattedResponse.punchList)
      .sort(
        (a, b) =>
          a.transcriptPosition.startIndex - b.transcriptPosition.startIndex,
      );

    let punchListItems = formattedResponse.punchList;

    // todo set into current video
    // todo type safety
    videoCreator.currentVideo!.punchList = punchListItems;

    videoCreator.punchListGenerateCount = punchListGenerateCount + 1;
  };

  const handleMatchWithSources = async (
    sources: string[],
    mode: 'replace-missing' | 'replace-all',
  ) => {
    setSelectedAction(null);
    if (!sources.length) {
      return;
    }
    try {
      // analytics
      analytics.track('feeling_lucky', {
        storyId: props.story?.id,
        storyTitle: props.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
        showcaseId: props.story?.primaryShowcase?.id,
        showcaseTitle: props.story?.primaryShowcase?.title,
      });
      setGeneratingMissingAiImage(true);
      await videoCreator.punchListManager.matchWithSources(sources, mode);
    } catch (error) {
      console.error('Feeling lucky error: ', error);
    } finally {
      setGeneratingMissingAiImage(false);
    }
  };

  const hasNonEmptyPunchListItems = (): boolean => {
    return punchList.some(
      item =>
        videoCreator.punchListManager.isAiGeneratedItem(item) &&
        !videoCreator.punchListManager.isEmptyItem(item),
    );
  };

  const hasEmptyPunchListItems = (): boolean => {
    return punchList.some(
      item =>
        videoCreator.punchListManager.isAiGeneratedItem(item) &&
        videoCreator.punchListManager.isEmptyItem(item),
    );
  };

  const getPunchListFromRawResponse = (): PunchListItem[] | undefined => {
    let result: PunchListItem[] | undefined;
    const rawResponse =
      videoCreator?.contentStudioGeneratedContent?.['Photo Punchlist']?.content
        ?.response;
    if (rawResponse && typeof rawResponse === 'string') {
      try {
        const resp = JSON.parse(rawResponse);
        const args = JSON.parse(resp?.function_call?.arguments);
        result = args?.photo_punchlist;
      } catch (error) {
        console.log('Failed to parse punchlist response', error);
      }
    } else {
      result = rawResponse as PunchListItem[] | undefined;
    }
    return result?.map(p => ({ ...p, id: uuid() }));
  };

  return (
    <Main>
      {(generatingMissingAiImage || videoCreator.punchListLoading) && (
        <SpinningLoading
          positionTop="20px"
          text={
            videoCreator.punchListLoading
              ? 'Hunting for photos to use'
              : undefined
          }
        />
      )}
      {videoCreator.savingStockPhoto && (
        <SpinningLoading positionTop="20px" text="Saving photo" />
      )}

      <ButtonGroup>
        <AIGenerateCard
          handleClick={async () => {
            setSelectedAction('photoHunt');
            await handleGeneratePhotoPunchList();
          }}
          unsetWidth={true}
          strokeColor={selectedAction !== 'photoHunt' ? '#484848' : ''}
          text={loading ? 'Generating...' : 'Photo Hunt'}
          disabled={loading}
        />
        <AIGenerateCard
          handleClick={async () => {
            setSelectedAction('feelingLucky');
          }}
          unsetWidth={true}
          text="Feeling Lucky"
          disabled={!punchList?.length || generatingMissingAiImage}
          Icon={
            <FeelingLuckyIcon
              strokeColor={punchList?.length ? '#17C964' : '#484848'}
            />
          }
        />
      </ButtonGroup>

      {/* {modalItem?.item && (
        <Modal
          isOpen={!!modalItem}
          onClose={() => toggleModal({ ...modalItem, item: null })}
          paddingHorizontal="0"
        >
          <TriggerPunchListGenerateModal
            modalItem={modalItem.item}
            onClose={() => toggleModal({ ...modalItem, item: null })}
            toggleGeneratedImages={toggleGeneratedImages}
            openGeneratedImages={openGeneratedImages}
          />
        </Modal>
      )} */}
      <GeneratedPunchListModal
        modalItem={modalItem}
        toggleModal={toggleModal}
      />
      <Content>
        {punchList
          ?.map(punchListItem => (
            <PunchListContentItem
              key={punchListItem.id}
              punchListItem={punchListItem}
              selectedPunchListId={selectedPunchListId}
              setSelectedPunchListId={setSelectedPunchListId}
              toggleModal={toggleModal}
            />
          ))
          .filter(Boolean)}
      </Content>
      {selectedAction === 'feelingLucky' && (
        <FeelingLuckyModal
          closeModal={() => setSelectedAction(null)}
          renderActions={({ sources, disabled }) =>
            hasNonEmptyPunchListItems() ? (
              <>
                <ProceedButton
                  onClick={() =>
                    handleMatchWithSources(sources, 'replace-missing')
                  }
                  disabled={disabled || !hasEmptyPunchListItems()}
                  key="replace-missing"
                >
                  <FeelingLuckyIcon strokeColor="#03041a" />
                  Replace Missing
                </ProceedButton>
                <ProceedButton
                  onClick={() => handleMatchWithSources(sources, 'replace-all')}
                  disabled={disabled}
                  key="replace-all"
                >
                  <FeelingLuckyIcon strokeColor="#03041a" />
                  Replace All
                </ProceedButton>
              </>
            ) : (
              <ProceedButton
                onClick={() => handleMatchWithSources(sources, 'replace-all')}
                disabled={disabled}
              >
                <FeelingLuckyIcon strokeColor="#03041a" />
                Generate
              </ProceedButton>
            )
          }
        />
      )}
    </Main>
  );
});

export default AIPhotoPunchListProducer;

const Main = styled.div``;
const Content = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 5px;
`;
